import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { saleService } from '../services/sale.service';

// Async thunks
export const fetchSales = createAsyncThunk(
  'sale/fetchSales',
  async (_, { rejectWithValue }) => {
    try {
      return await saleService.getAllSales();
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erreur lors de la récupération des ventes');
    }
  }
);

export const fetchActiveSales = createAsyncThunk(
  'sale/fetchActiveSales',
  async (_, { rejectWithValue }) => {
    try {
      return await saleService.getActiveSales();
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erreur lors de la récupération des ventes actives');
    }
  }
);

export const fetchSaleById = createAsyncThunk(
  'sale/fetchSaleById',
  async (id, { rejectWithValue }) => {
    try {
      return await saleService.getSaleById(id);
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erreur lors de la récupération de la vente');
    }
  }
);

export const fetchSalesByCustomer = createAsyncThunk(
  'sale/fetchSalesByCustomer',
  async (customerId, { rejectWithValue }) => {
    try {
      return await saleService.getSalesByCustomer(customerId);
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erreur lors de la récupération des ventes du client');
    }
  }
);

export const fetchSalesByDateRange = createAsyncThunk(
  'sale/fetchSalesByDateRange',
  async ({ startDate, endDate }, { rejectWithValue }) => {
    try {
      return await saleService.getSalesByDateRange(startDate, endDate);
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erreur lors de la récupération des ventes');
    }
  }
);

export const searchSales = createAsyncThunk(
  'sale/searchSales',
  async (query, { rejectWithValue }) => {
    try {
      return await saleService.searchSales(query);
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erreur lors de la recherche de ventes');
    }
  }
);

export const addSale = createAsyncThunk(
  'sale/addSale',
  async (sale, { rejectWithValue }) => {
    try {
      return await saleService.createSale(sale);
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erreur lors de la création de la vente');
    }
  }
);

export const editSale = createAsyncThunk(
  'sale/editSale',
  async ({ id, saleData }, { rejectWithValue }) => {
    try {
      return await saleService.updateSale(id, saleData);
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erreur lors de la mise à jour de la vente');
    }
  }
);

export const removeSale = createAsyncThunk(
  'sale/removeSale',
  async (id, { rejectWithValue }) => {
    try {
      await saleService.deleteSale(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erreur lors de la suppression de la vente');
    }
  }
);

export const cancelSale = createAsyncThunk(
  'sale/cancelSale',
  async (id, { rejectWithValue }) => {
    try {
      const result = await saleService.cancelSale(id);
      return result ?? { saleId: id };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erreur lors annulation de la vente');
    }
  }
);

export const transformSaleToInvoice = createAsyncThunk(
  'sale/transformSaleToInvoice',
  async ({ ids, includesFiscalTimber }, { rejectWithValue }) => {
    try {
      return await saleService.transformSaleToInvoice(ids, includesFiscalTimber);
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erreur lors de la transformation en facture');
    }
  }
);

export const addPaymentsToSales = createAsyncThunk(
  'sale/addPaymentsToSales',
  async (dto, { rejectWithValue }) => {
    try {
      return await saleService.addPaymentsToSales(dto);
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Erreur lors de l\'ajout des paiements');
    }
  }
);

// Helpers
const extractSalesArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  return [];
};

const extractTotalCount = (payload) => {
  if (typeof payload?.totalCount === 'number') return payload.totalCount;
  if (Array.isArray(payload)) return payload.length;
  if (Array.isArray(payload?.items)) return payload.items.length;
  return 0;
};

const getSaleId = (sale) => sale?.saleId ?? sale?.id;

const upsertSaleInList = (sales, updatedSale) => {
  const updatedId = getSaleId(updatedSale);
  if (!updatedId) return sales;

  const index = sales.findIndex((sale) => getSaleId(sale) === updatedId);
  if (index === -1) return sales;

  const nextSales = [...sales];
  nextSales[index] = {
    ...nextSales[index],
    ...updatedSale,
  };
  return nextSales;
};

// const removeSalesFromList = (sales, ids = []) => {
//   const idSet = new Set(ids.map((id) => String(id)));
//   return sales.filter((sale) => !idSet.has(String(getSaleId(sale))));
// };

const markSaleAsCancelled = (sale, payload, cancelledId) => ({
  ...sale,
  ...(payload && typeof payload === 'object' ? payload : {}),
  saleId: getSaleId(payload) ?? cancelledId ?? getSaleId(sale),
  statusName:
    payload?.statusName ||
    sale?.statusName ||
    'Vente annulée',
});

// Initial state
const initialState = {
  sales: [],
  totalCount: 0,
  searchResults: [],
  selectedSale: null,
  loading: false,
  searchLoading: false,
  error: null,
  showModal: false,
  showDeleteModal: false,
  searchQuery: '',
  lastInvoiceJob: null,
};

// Slice
const saleSlice = createSlice({
  name: 'sale',
  initialState,
  reducers: {
    openModal: (state) => {
      state.showModal = true;
    },
    closeModal: (state) => {
      state.showModal = false;
    },
    openDeleteModal: (state) => {
      state.showDeleteModal = true;
    },
    closeDeleteModal: (state) => {
      state.showDeleteModal = false;
    },
    clearError: (state) => {
      state.error = null;
    },
    setSelectedSale: (state, action) => {
      state.selectedSale = action.payload;
    },
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },
    clearSearchResults: (state) => {
      state.searchResults = [];
      state.searchQuery = '';
    },
    saleInvoiceSocketSuccess: (state, action) => {
      state.lastInvoiceJob = {
        jobId: action.payload?.jobId,
        saleIds: action.payload?.saleIds || [],
        invoiceId: action.payload?.invoiceId || null,
        invoiceNumber: action.payload?.invoiceNumber || null,
        result: action.payload?.result || null,
        success: true,
        error: null,
      };
    },

    saleInvoiceSocketFailed: (state, action) => {
      state.lastInvoiceJob = {
        jobId: action.payload?.jobId,
        saleIds: action.payload?.saleIds || [],
        invoiceId: null,
        invoiceNumber: null,
        result: null,
        success: false,
        error: action.payload?.error || 'Une erreur est survenue.',
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch sales
      .addCase(fetchSales.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSales.fulfilled, (state, action) => {
        state.loading = false;
        state.sales = extractSalesArray(action.payload);
        state.totalCount = extractTotalCount(action.payload);
      })
      .addCase(fetchSales.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch active sales
      .addCase(fetchActiveSales.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchActiveSales.fulfilled, (state, action) => {
        state.loading = false;
        state.sales = extractSalesArray(action.payload);
        state.totalCount = extractTotalCount(action.payload);
      })
      .addCase(fetchActiveSales.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch sale by ID
      .addCase(fetchSaleById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSaleById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedSale = action.payload;
      })
      .addCase(fetchSaleById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch sales by customer
      .addCase(fetchSalesByCustomer.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSalesByCustomer.fulfilled, (state, action) => {
        state.loading = false;
        state.sales = extractSalesArray(action.payload);
        state.totalCount = extractTotalCount(action.payload);
      })
      .addCase(fetchSalesByCustomer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch sales by date range
      .addCase(fetchSalesByDateRange.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSalesByDateRange.fulfilled, (state, action) => {
        state.loading = false;
        state.sales = extractSalesArray(action.payload);
        state.totalCount = extractTotalCount(action.payload);
      })
      .addCase(fetchSalesByDateRange.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Search sales
      .addCase(searchSales.pending, (state) => {
        state.searchLoading = true;
        state.error = null;
      })
      .addCase(searchSales.fulfilled, (state, action) => {
        state.searchLoading = false;
        state.searchResults = extractSalesArray(action.payload);
      })
      .addCase(searchSales.rejected, (state, action) => {
        state.searchLoading = false;
        state.error = action.payload;
      })

      // Add sale
      .addCase(addSale.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addSale.fulfilled, (state, action) => {
        state.loading = false;
        state.sales.unshift(action.payload);
        state.totalCount += 1;
        state.showModal = false;
      })
      .addCase(addSale.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Edit sale
      .addCase(editSale.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editSale.fulfilled, (state, action) => {
        state.loading = false;
        state.sales = upsertSaleInList(state.sales, action.payload);

        if (state.selectedSale && getSaleId(state.selectedSale) === getSaleId(action.payload)) {
          state.selectedSale = {
            ...state.selectedSale,
            ...action.payload,
          };
        }

        state.showModal = false;
      })
      .addCase(editSale.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Remove sale
      .addCase(removeSale.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeSale.fulfilled, (state, action) => {
        state.loading = false;
        state.sales = state.sales.filter((sale) => getSaleId(sale) !== action.payload);
        state.totalCount = Math.max(0, state.totalCount - 1);

        if (state.selectedSale && getSaleId(state.selectedSale) === action.payload) {
          state.selectedSale = null;
        }

        state.showDeleteModal = false;
      })
      .addCase(removeSale.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Cancel sale
      .addCase(cancelSale.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cancelSale.fulfilled, (state, action) => {
        state.loading = false;

        const cancelledId = getSaleId(action.payload) ?? action.meta.arg;
        const existingSale = state.sales.find((sale) => getSaleId(sale) === cancelledId);

        if (existingSale) {
          const cancelledSale = markSaleAsCancelled(existingSale, action.payload, cancelledId);
          state.sales = upsertSaleInList(state.sales, cancelledSale);
        }

        if (state.selectedSale && getSaleId(state.selectedSale) === cancelledId) {
          state.selectedSale = markSaleAsCancelled(state.selectedSale, action.payload, cancelledId);
        }

        state.showDeleteModal = false;
      })
      .addCase(cancelSale.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Transform sales to invoice
      .addCase(transformSaleToInvoice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(transformSaleToInvoice.fulfilled, (state) => {
        state.loading = false;

      
      })
      .addCase(transformSaleToInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Add payments
      .addCase(addPaymentsToSales.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addPaymentsToSales.fulfilled, (state, action) => {
        state.loading = false;

        const updatedSales = Array.isArray(action.payload)
          ? action.payload
          : Array.isArray(action.payload?.sales)
            ? action.payload.sales
            : action.payload
              ? [action.payload]
              : [];

        updatedSales.forEach((updatedSale) => {
          state.sales = upsertSaleInList(state.sales, updatedSale);

          if (
            state.selectedSale &&
            getSaleId(state.selectedSale) === getSaleId(updatedSale)
          ) {
            state.selectedSale = {
              ...state.selectedSale,
              ...updatedSale,
            };
          }
        });
      })
      .addCase(addPaymentsToSales.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});



export const {
  openModal,
  closeModal,
  openDeleteModal,
  closeDeleteModal,
  clearError,
  setSelectedSale,
  setSearchQuery,
  clearSearchResults,
  saleInvoiceSocketSuccess,
  saleInvoiceSocketFailed,
} = saleSlice.actions;

export default saleSlice.reducer;